import { useCallback, useRef, useState } from 'react'
import { showRegionEditModal } from './RegionEditModal'

export function useRegionCreator(moduleName, containerRef, onCreate) {
  const offsets = useRef({ x: 0, y: 0 })
  const absolutePosition = useRef({ x: 0, y: 0 })
  const [prototype, setPrototype] = useState(null)

  /** @type {(e: React.MouseEvent) => void} */
  const onMouseDown = useCallback(
    e => {
      if (e.target !== containerRef.current) {
        return
      }

      // The container may not be at 0,0 in the viewport, get its visual position relative to the viewport
      offsets.current = containerRef.current.getBoundingClientRect()

      // Calculate true viewport locations
      const x = e.clientX - offsets.current.x
      const y = e.clientY - offsets.current.y
      absolutePosition.current.x = x
      absolutePosition.current.y = y

      setPrototype({
        name: '',
        x,
        y,
        width: 0,
        height: 0
      })
    },
    [containerRef]
  )

  /** @type {(e: React.MouseEvent) => void} */
  const onMouseMove = useCallback(
    e => {
      if (!prototype) {
        return
      }

      const width = e.clientX - offsets.current.x - absolutePosition.current.x
      const height = e.clientY - offsets.current.y - absolutePosition.current.y
      const x =
        width >= 0
          ? absolutePosition.current.x
          : absolutePosition.current.x + width
      const y =
        height >= 0
          ? absolutePosition.current.y
          : absolutePosition.current.y + height

      setPrototype(prototype => ({
        ...prototype,
        x,
        y,
        width: Math.abs(width),
        height: Math.abs(height)
      }))
    },
    [prototype]
  )

  /** @type {(e: React.MouseEvent) => void} */
  const onMouseUp = useCallback(
    async e => {
      if (!prototype) {
        return
      }

      const width = e.clientX - offsets.current.x - absolutePosition.current.x
      const height = e.clientY - offsets.current.y - absolutePosition.current.y
      const x =
        width >= 0
          ? absolutePosition.current.x
          : absolutePosition.current.x + width
      const y =
        height >= 0
          ? absolutePosition.current.y
          : absolutePosition.current.y + height

      const region = await showRegionEditModal(moduleName, {
        ...prototype,
        x,
        y,
        width: Math.abs(width),
        height: Math.abs(height)
      })

      if (region !== false) {
        onCreate(region)
      }
      setPrototype(null)
    },
    [moduleName, onCreate, prototype]
  )

  return {
    prototype,
    onMouseDown,
    onMouseMove,
    onMouseUp
  }
}
